/**
 * Revenue Customer Split Component
 *
 * Shows revenue breakdown by new vs returning customers
 */

import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { LockedChartCard } from "./locked-chart-card";
import { MetricTooltip } from "./metric-tooltip";

interface RevenueCustomerSplitProps {
  current: {
    new: number;
    returning: number;
  } | null;
  previous: {
    new: number;
    returning: number;
  } | null;
}

export function RevenueCustomerSplit({ current, previous }: RevenueCustomerSplitProps) {
  const { t } = useTranslation("reports");

  // If data is null (basic tier), show locked version with mock data
  if (!current || !previous) {
    const mockContent = (
      <div className="space-y-6">
        {/* Mock Current Period */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Current Period</span>
            <span className="text-sm font-semibold">€0,000</span>
          </div>
          <div className="relative h-10 bg-muted rounded-lg overflow-hidden flex">
            <div className="bg-blue-500 w-1/2" />
            <div className="bg-green-500 w-1/2" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-blue-500" />
              <div>
                <div className="text-xs text-muted-foreground">New Customers</div>
                <div className="text-sm font-semibold">€0,000</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-green-500" />
              <div>
                <div className="text-xs text-muted-foreground">Returning Customers</div>
                <div className="text-sm font-semibold">€0,000</div>
              </div>
            </div>
          </div>
        </div>

        {/* Mock Previous Period */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-muted-foreground">Previous Period</span>
            <span className="text-sm font-semibold text-muted-foreground">€0,000</span>
          </div>
          <div className="relative h-10 bg-muted rounded-lg overflow-hidden flex">
            <div className="bg-blue-500 w-1/2" />
            <div className="bg-green-500 w-1/2" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-blue-500" />
              <div>
                <div className="text-xs text-muted-foreground">New Customers</div>
                <div className="text-sm font-semibold text-muted-foreground">€0,000</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-green-500" />
              <div>
                <div className="text-xs text-muted-foreground">Returning Customers</div>
                <div className="text-sm font-semibold text-muted-foreground">€0,000</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <LockedChartCard
        title={t("charts.revenueByCustomerType.title")}
        tooltip={t("charts.revenueByCustomerType.tooltip")}
        mockContent={mockContent}
        height="h-full"
      />
    );
  }

  const currentTotal = current.new + current.returning;
  const previousTotal = previous.new + previous.returning;

  const currentNewPercentage = currentTotal > 0 ? (current.new / currentTotal) * 100 : 0;
  const currentReturningPercentage =
    currentTotal > 0 ? (current.returning / currentTotal) * 100 : 0;

  const previousNewPercentage = previousTotal > 0 ? (previous.new / previousTotal) * 100 : 0;
  const previousReturningPercentage =
    previousTotal > 0 ? (previous.returning / previousTotal) * 100 : 0;

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 h-full">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
          {t("charts.revenueByCustomerType.title")}
          <MetricTooltip content={t("charts.revenueByCustomerType.tooltip")} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Current Period */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">{t("period.current")}</span>
              <span className="text-sm font-semibold">
                <PriceDisplay amount={currentTotal} />
              </span>
            </div>

            {/* Stacked bar for current period */}
            <div className="relative h-10 bg-muted rounded-lg overflow-hidden flex">
              <div
                className="bg-blue-500 flex items-center justify-center text-xs font-medium text-white transition-all duration-1000"
                style={{ width: `${currentNewPercentage}%` }}
              >
                {currentNewPercentage > 15 && `${currentNewPercentage.toFixed(0)}%`}
              </div>
              <div
                className="bg-green-500 flex items-center justify-center text-xs font-medium text-white transition-all duration-1000"
                style={{ width: `${currentReturningPercentage}%` }}
              >
                {currentReturningPercentage > 15 && `${currentReturningPercentage.toFixed(0)}%`}
              </div>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-blue-500" />
                <div>
                  <div className="text-xs text-muted-foreground">
                    {t("charts.revenueByCustomerType.newCustomers")}
                  </div>
                  <div className="text-sm font-semibold">
                    <PriceDisplay amount={current.new} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-green-500" />
                <div>
                  <div className="text-xs text-muted-foreground">
                    {t("charts.revenueByCustomerType.returningCustomers")}
                  </div>
                  <div className="text-sm font-semibold">
                    <PriceDisplay amount={current.returning} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Previous Period */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">
                {t("period.previous")}
              </span>
              <span className="text-sm font-semibold text-muted-foreground">
                <PriceDisplay amount={previousTotal} />
              </span>
            </div>

            {/* Stacked bar for previous period */}
            <div className="relative h-10 bg-muted rounded-lg overflow-hidden flex">
              <div
                className="bg-blue-500 flex items-center justify-center text-xs font-medium text-white transition-all duration-1000"
                style={{ width: `${previousNewPercentage}%` }}
              >
                {previousNewPercentage > 15 && `${previousNewPercentage.toFixed(0)}%`}
              </div>
              <div
                className="bg-green-500 flex items-center justify-center text-xs font-medium text-white transition-all duration-1000"
                style={{ width: `${previousReturningPercentage}%` }}
              >
                {previousReturningPercentage > 15 && `${previousReturningPercentage.toFixed(0)}%`}
              </div>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-blue-500" />
                <div>
                  <div className="text-xs text-muted-foreground">
                    {t("charts.revenueByCustomerType.newCustomers")}
                  </div>
                  <div className="text-sm font-semibold text-muted-foreground">
                    <PriceDisplay amount={previous.new} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-green-500" />
                <div>
                  <div className="text-xs text-muted-foreground">
                    {t("charts.revenueByCustomerType.returningCustomers")}
                  </div>
                  <div className="text-sm font-semibold text-muted-foreground">
                    <PriceDisplay amount={previous.returning} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
